import React, { useEffect, useMemo, useState } from 'react';

import { Link } from 'react-router-dom';

import { useProducts } from '../useProducts';

import { useSiteSettings } from '../SiteSettingsContext';

import { SITE_SETTINGS_DEFAULTS } from '../siteSettingsDefaults';

import { resolveFeaturedProducts } from '../featuredProducts';

import StorefrontHeroActions from './StorefrontHeroActions';

import { resolveMediaUrl } from '../productMediaUtils';

import { pageCopySection } from '../content/siteCopyHelpers';

import { HOME_COPY_DEFAULTS } from '../content/pageCopyDefaults';

import { brandNameFromSettings } from '../siteBrand';

import { heroImageBackgroundPosition, normalizeHeroImageFocal } from '../heroImageFocal';



const TRUST_ICONS = ['spark', 'ship', 'pin'];



function TrustIcon({ type }) {

  if (type === 'ship') {

    return (

      <svg className="home-hero__trust-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>

        <path d="M3 10h13v8H3zM16 12h2l3 3v3h-5v-6zM6 18a2 2 0 1 0 0 .01M18 18a2 2 0 1 0 0 .01" />

      </svg>

    );

  }

  if (type === 'pin') {

    return (

      <svg className="home-hero__trust-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>

        <path d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z" />

        <circle cx="12" cy="10" r="2.5" />

      </svg>

    );

  }

  return (

    <svg className="home-hero__trust-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>

      <path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7L12 16.8 5.7 21l2.3-7-6-4.6h7.6L12 2z" />

    </svg>

  );

}



function useHeroImage(site, allProducts, catalogLoading) {

  return useMemo(() => {

    const og = String(site.seo_home_og_image || '').trim();

    if (og) return resolveMediaUrl(og);

    if (catalogLoading && allProducts.length === 0) return '';

    const featured = resolveFeaturedProducts(allProducts, site.featured_products, 1);

    if (featured[0]?.image) return resolveMediaUrl(featured[0].image);

    const withImage = (allProducts || []).find((p) => String(p?.image || '').trim());

    return withImage ? resolveMediaUrl(withImage.image) : '';

  }, [allProducts, site.featured_products, site.seo_home_og_image, catalogLoading]);

}



/**

 * Split editorial home hero — copy on light panel, large photo column (Shopify image-with-text).

 */

export default function StorefrontHero({ className = '', compact = false }) {

  const { allProducts, categories, loading: catalogLoading } = useProducts();

  const { settings: site } = useSiteSettings();

  const [imageReady, setImageReady] = useState(false);



  const heroImage = useHeroImage(site, allProducts, catalogLoading);



  const homeCopy = useMemo(() => pageCopySection(site.page_copy, 'home'), [site.page_copy]);



  const trustItems = useMemo(() => {
    if (compact) return [];
    const locFallback = site.footer_location || site.location_line || 'India';
    const labels = [
      homeCopy.hero_trust_1 || HOME_COPY_DEFAULTS.hero_trust_1,
      homeCopy.hero_trust_2 || HOME_COPY_DEFAULTS.hero_trust_2,
      String(homeCopy.hero_trust_3 || '').trim() || locFallback,
    ];
    return labels
      .map((label, i) => ({ key: `trust-${i}`, label, icon: TRUST_ICONS[i] }))
      .filter((item) => String(item.label || '').trim());
  }, [compact, homeCopy, site.footer_location, site.location_line]);

  const heroBadge = compact ? '' : homeCopy.hero_badge || HOME_COPY_DEFAULTS.hero_badge;

  const heroFocal = useMemo(

    () =>

      normalizeHeroImageFocal({

        x: homeCopy.hero_image_focal_x,

        y: homeCopy.hero_image_focal_y,

      }),

    [homeCopy.hero_image_focal_x, homeCopy.hero_image_focal_y],

  );

  const imagePosition = heroImageBackgroundPosition(heroFocal);

  const brandName = brandNameFromSettings(site);



  useEffect(() => {

    if (!heroImage) {

      setImageReady(false);

      return undefined;

    }

    setImageReady(false);

    let cancelled = false;

    const img = new Image();

    img.onload = () => {

      if (!cancelled) setImageReady(true);

    };

    img.onerror = () => {

      if (!cancelled) setImageReady(false);

    };

    img.src = heroImage;

    return () => {

      cancelled = true;

      img.onload = null;

      img.onerror = null;

    };

  }, [heroImage]);



  useEffect(() => {

    if (!heroImage) return undefined;

    const link = document.createElement('link');

    link.rel = 'preload';

    link.as = 'image';

    link.href = heroImage;

    document.head.appendChild(link);

    return () => link.remove();

  }, [heroImage]);



  const title = site.hero_title || SITE_SETTINGS_DEFAULTS.hero_title;

  const subtitle = site.hero_subtitle || SITE_SETTINGS_DEFAULTS.hero_subtitle;

  return (

    <section

      className={`home-hero home-hero--split${compact ? ' home-hero--compact' : ''}${imageReady ? ' home-hero--ready' : ''} ${className}`.trim()}

      aria-label="Welcome"

    >

      <div className="home-hero__ambient" aria-hidden="true">

        <span className="home-hero__blob home-hero__blob--1" />

        <span className="home-hero__blob home-hero__blob--2" />

      </div>



      <div className="home-hero__split">

        <div className="home-hero__copy-col">

          <div className="home-hero__copy">

            {!compact ? <p className="home-hero__eyebrow">{brandName}</p> : null}

            <h1 className="home-hero__title">{title}</h1>

            {subtitle ? (
              <p
                className={
                  compact ? 'home-hero__subtitle home-hero__subtitle--compact' : 'home-hero__subtitle handwritten'
                }
              >
                {subtitle}
              </p>
            ) : null}

            <StorefrontHeroActions
              placement="home"
              compact={compact}
              className={
                compact
                  ? 'home-hero__actions hero-actions shop-hero-actions d-flex flex-wrap align-items-center gap-2 mt-2'
                  : 'home-hero__actions hero-actions shop-hero-actions d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 gap-sm-3'
              }
              giftFinderPlacement={compact ? null : 'home'}
              giftFinderClassName="btn btn-outline-secondary hero-secondary-cta"
              primaryBtnClass={compact ? 'btn btn-primary hero-cta' : 'btn btn-primary hero-cta btn-lg'}
              secondaryBtnClass={
                compact ? 'btn btn-outline-secondary hero-secondary-cta btn-sm' : 'btn btn-outline-secondary hero-secondary-cta btn-lg'
              }
            />

            {trustItems.length > 0 ? (
              <ul className="home-hero__trust" aria-label="Store highlights">
                {trustItems.map((item) => (
                  <li key={item.key} className="home-hero__trust-item">
                    <TrustIcon type={item.icon} />
                    {item.label}
                  </li>
                ))}
              </ul>
            ) : null}



            {!catalogLoading && categories.length === 0 ? (

              <p className="home-hero__admin-hint mb-0">Connect MySQL and add products in Admin to load categories.</p>

            ) : null}

          </div>

        </div>



        <div className="home-hero__media-col">

          <Link to="/shop" className="home-hero__media-link text-decoration-none" aria-label="Browse shop">

            <div className={`home-hero__media${imageReady ? ' is-ready' : ''}`}>

              {heroImage ? (

                <img

                  key={heroImage}

                  src={heroImage}

                  alt={title}

                  className="home-hero__media-img"

                  style={{ objectPosition: imagePosition }}

                  decoding="async"

                />

              ) : (

                <div className="home-hero__media-placeholder" aria-hidden />

              )}

              {heroBadge ? <span className="home-hero__badge">{heroBadge}</span> : null}

            </div>

          </Link>

        </div>

      </div>

    </section>

  );

}


